import {BrowserModule} from '@angular/platform-browser';
import {UIRouterModule} from '@uirouter/angular';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {TranslateModule} from '@ngx-translate/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ReactiveFormsModule} from '@angular/forms';
import {NgModule} from '@angular/core';
import {
    APP_NAME,
    CircuitStatusService,
    CREDENTIAL_TYPE,
    CredentialType,
    DefaultState,
    ENTITY_REF,
    EnvironmentProvider,
    GroupsProvider,
    LocaleConfigProvider,
    NOTIFICATION_DISPLAY,
    NotificationDisplayType,
    PesDashboardConfigProvider,
    PesStepProvider,
    PesUiModule,
    PesUserService,
    routerConfig,
    StateProvider
} from '@pes/ui';
import {AppComponent} from './app.component';
import {environment} from '../environments/environment';
import {AppGroupsProvider} from './config/provider/app-rights.provider';
import {APP_STATES} from './config/app.states';
import {AppLocaleConfigProvider} from './config/provider/app-locale-config.provider';
import {StepSignatureEdocComponent} from './component/step-signature-edoc/step-signature-edoc.component';
import {StepValidateSequentialComponent} from './component/step-validate-sequential/step-validate-sequential.component';
import {LbpaTaskComponent} from './component/task/lbpa-task.component';
import {LbpaStepValidateComponent} from './component/step-validate/step-validate.component';
import {LoginComponent} from './component/pes-login/login.component';
import {NgxQRCodeModule} from 'ngx-qrcode3';
import {QRCodeDialogComponent} from './component/qr-code/qr-code-dialog.component';
import {MessageComponent} from './component/pes-message/pes-message.component';
import {AppStateProvider} from './config/provider/state.provider';
import {CircuitsFrontOfficeComponent} from './component/circuit-list-front-office/circuits-front-office.component';
import {BackOfficeDashboardComponent} from './component/back-office-dashboard/back-office-dashboard.component';
import {UserService} from './config/provider/user.service';
import {EdocTransactionListComponent} from './component/edoc-transaction-list/edoc-transaction-list.component';
import {LbpaAppHeaderComponent} from './component/app-header/lbpa-app-header.component';
import {LbpaCircuitDetailsComponent} from './component/lbpa-circuit-details/lbpa-circuit-details.component';
import {LbpaTimelineComponent} from './component/lbpa-timeline/lbpa-timeline.component';
import {WelcomeDialogComponent} from './component/dialog/welcome-dialog/welcome-dialog.component';
import {LbpaDateFilterComponent} from './component/lbpa-date-filter/lbpa-date-filter.component';
import {LbpaWizardComponent} from './component/lbpa-wizard/lbpa-wizard.component';
import {LbpaBreadcrumbComponent} from './component/lbpa-breadcrumb/lbpa-breadcrumb.component';
import {DashboardConfigProvider} from './config/provider/dashboard-config.provider';
import {LbpaCircuitStatusService} from './config/provider/circuit-status.service';
import {AppStepProvider} from './config/provider/app.step-provider';
import { DeviceDetectorModule } from 'ngx-device-detector';
import { StepSignatureParcoursLbpaComponent } from './component/step-parcours-lbpa/step-signature-parcours-lbpa.component';
import {WelcomeComponent} from './component/step-parcours-lbpa/welcome/welcome.component';
import { ValidateComponent } from './component/step-parcours-lbpa/validate/validate.component';
import { LbpaFilArianeComponent } from './component/lbpa-fil-ariane/lbpa-fil-ariane.component';
import { SignComponent } from './component/step-parcours-lbpa/sign/sign.component';
import { UserActionTracerService } from './utils/service/user-action-tracer.service';
import { LbpaEdocComponent } from './component/lbpa-edoc/lbpa-edoc.component';
import {TagCommanderService} from './utils/service/tag-commander.service';
import { LbpaFooterComponent } from './component/lbpa-footer/lbpa-footer.component';
import {SafeHtmlPipe} from './utils/pipe/safe-html.pipe';
import {HtmReplacePipe} from './utils/pipe/htm-replace.pipe';

/**
 * The PES default app module.
 */
@NgModule({
    declarations: [
        AppComponent,
        StepSignatureEdocComponent,
        StepValidateSequentialComponent,
        LbpaTaskComponent,
        LbpaStepValidateComponent,
        LoginComponent,
        QRCodeDialogComponent,
        MessageComponent,
        CircuitsFrontOfficeComponent,
        BackOfficeDashboardComponent,
        EdocTransactionListComponent,
        LbpaAppHeaderComponent,
        LbpaCircuitDetailsComponent,
        LbpaTimelineComponent,
        WelcomeDialogComponent,
        LbpaDateFilterComponent,
        LbpaWizardComponent,
        LbpaBreadcrumbComponent,
        WelcomeComponent,
        StepSignatureParcoursLbpaComponent,
        ValidateComponent,
        LbpaFilArianeComponent,
        SignComponent,
        LbpaEdocComponent,
        LbpaFooterComponent,
        SafeHtmlPipe,
        HtmReplacePipe
    ],
    entryComponents: [
        StepSignatureEdocComponent,
        StepValidateSequentialComponent,
        LbpaTaskComponent,
        LbpaStepValidateComponent,
        QRCodeDialogComponent,
        MessageComponent,
        CircuitsFrontOfficeComponent,
        BackOfficeDashboardComponent,
        EdocTransactionListComponent,
        LbpaAppHeaderComponent,
        WelcomeDialogComponent,
        WelcomeComponent,
        ValidateComponent,
        LbpaFilArianeComponent,
        SignComponent,
        LbpaEdocComponent,
        StepSignatureParcoursLbpaComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        PesUiModule.forRoot(),
        NgbModule.forRoot(),
        TranslateModule.forRoot(),
        UIRouterModule.forRoot({
            states: [
                ...APP_STATES
            ],
            useHash: false,
            otherwise: {state: DefaultState.LOGIN},
            config: routerConfig
        }),
        NgxQRCodeModule,
        DeviceDetectorModule.forRoot()
    ],
    providers: [
        AppStateProvider,
        UserActionTracerService,
        TagCommanderService,
        // Entity reference
        {provide: ENTITY_REF, useValue: 'LBPAS'},
        // Application name
        {provide: APP_NAME, useValue: 'La Banque Postale Assurance'},
        // login type
        {provide: CREDENTIAL_TYPE, useValue: CredentialType.LOGIN},
        // App locales config
        {provide: LocaleConfigProvider, useClass: AppLocaleConfigProvider},
        // Provides environment information
        {provide: EnvironmentProvider, useValue: environment},
        // Rights config
        {provide: GroupsProvider, useClass: AppGroupsProvider},
        {provide: PesUserService, useClass: UserService},
        {provide: StateProvider, useClass: AppStateProvider},
        {provide: PesStepProvider, useClass: AppStepProvider},
        {provide: PesDashboardConfigProvider, useClass: DashboardConfigProvider},
        {provide: CircuitStatusService, useClass: LbpaCircuitStatusService},
        {provide: NOTIFICATION_DISPLAY, useValue: NotificationDisplayType.BOX}

    ],
    bootstrap: [AppComponent]
})
export class AppModule {

    /**
     * Creates a new AppModule instance.
     *
     * @param {LocaleConfigProvider} locales the reference to the PES LocaleConfigProvider singleton, used for locales
     *                                       initialization.
     */
    constructor(private readonly locales: LocaleConfigProvider) {
        this.locales.init();
    }
}



// WEBPACK FOOTER //
// ./src/app/app.module.ts